import { useState } from 'react';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useConversationStore, useUIStore } from '../../../stores';
import { Spinner } from '../../../components';
import { useCustomer } from '../hooks/useCustomer';
import { useIsDesktop } from '../../../lib/hooks/useMediaQuery';
import CustomerProfile from './CustomerProfile';
import RecentOrders from './RecentOrders';
import CustomerNotes from './CustomerNotes';
import CreateOrderModal from './CreateOrderModal';
import OrderDetailModal from './OrderDetailModal';
import { ClerkCustomerInfo, useClerkOpsEnabled } from '../../clerk-ops';

export default function CustomerPanel() {
  const { customerPanelOpen, setCustomerPanelOpen, setMobileView } = useUIStore();
  const { selectedConversationId, getConversation } = useConversationStore();
  const isDesktop = useIsDesktop();

  const conversation = selectedConversationId
    ? getConversation(selectedConversationId)
    : null;

  const { customer, orders, notes, isLoading, addNote, createOrder, isCreatingOrder } = useCustomer(
    conversation?.customerId || null
  );
  // Service-business stores (Clerk) show business context instead of orders.
  const { data: businessMode } = useClerkOpsEnabled();
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // On desktop, respect the toggle. On mobile, DashboardPage controls visibility.
  if (isDesktop && !customerPanelOpen) return null;

  if (!conversation) {
    return (
      <div className="w-full border-l border-line bg-surface p-6 lg:w-80 lg:flex-shrink-0">
        <p className="text-sm text-ink-3">Select a conversation to view customer info</p>
      </div>
    );
  }

  const handleClose = () => {
    if (isDesktop) {
      setCustomerPanelOpen(false);
    } else {
      setMobileView('chat');
    }
  };

  return (
    <div className="flex w-full flex-col border-l border-line bg-surface lg:w-80 lg:flex-shrink-0">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line p-4">
        <div className="flex items-center gap-2">
          {!isDesktop && (
            <button
              onClick={() => setMobileView('chat')}
              className="rounded-xl p-1 text-ink-2 hover:bg-black/[0.04]"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
          )}
          <h3 className="font-semibold text-ink">Customer Info</h3>
        </div>
        <button
          onClick={handleClose}
          className="rounded-xl p-1 text-ink-2 hover:bg-black/[0.04]"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-6 overflow-y-auto p-4 scrollbar-thin">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <>
            {customer && (
              <CustomerProfile customer={customer} showCommerceMetrics={!businessMode} />
            )}
            {businessMode ? (
              <ClerkCustomerInfo customerId={conversation.customerId} />
            ) : (
              <>
                <RecentOrders
                  orders={orders}
                  onCreateOrder={() => setShowCreateOrder(true)}
                  onViewOrder={setSelectedOrderId}
                />
                <CreateOrderModal
                  open={showCreateOrder}
                  onClose={() => setShowCreateOrder(false)}
                  onSubmit={createOrder}
                  isSubmitting={isCreatingOrder}
                />
                <OrderDetailModal
                  open={!!selectedOrderId}
                  onClose={() => setSelectedOrderId(null)}
                  customerId={conversation.customerId}
                  orderId={selectedOrderId}
                />
              </>
            )}
            <CustomerNotes notes={notes} onAddNote={addNote} />
          </>
        )}
      </div>
    </div>
  );
}
